import { COUNTRY_SHORTHANDS, UNITED_STATES } from './countries';
import type { Env } from './types';

export const ALLOWED_COUNTRIES = [COUNTRY_SHORTHANDS.UNITED_STATES] as const;

/**
 * States that control the visibility of the cookie settings toggler in the UI.
 * The actual opt-out drawer content and consent behavior are configured
 * via geolocation rules in the OneTrust dashboard.
 */
export const ALLOWED_STATES = [
  UNITED_STATES.states.CA.shorthand,
  UNITED_STATES.states.CO.shorthand,
  UNITED_STATES.states.CT.shorthand,
  UNITED_STATES.states.DE.shorthand,
  UNITED_STATES.states.FL.shorthand,
  UNITED_STATES.states.IA.shorthand,
  UNITED_STATES.states.IN.shorthand,
  UNITED_STATES.states.KY.shorthand,
  UNITED_STATES.states.MD.shorthand,
  UNITED_STATES.states.MN.shorthand,
  UNITED_STATES.states.MT.shorthand,
  UNITED_STATES.states.NE.shorthand,
  UNITED_STATES.states.NH.shorthand,
  UNITED_STATES.states.NJ.shorthand,
  UNITED_STATES.states.OR.shorthand,
  UNITED_STATES.states.RI.shorthand,
  UNITED_STATES.states.TN.shorthand,
  UNITED_STATES.states.TX.shorthand,
  UNITED_STATES.states.UT.shorthand,
  UNITED_STATES.states.VA.shorthand,
] as const;

export type AllowedCountry = (typeof ALLOWED_COUNTRIES)[number];
export type AllowedState = (typeof ALLOWED_STATES)[number];

export const oneTrustDomainScriptIds = {
  TEST: 'a6170136-4755-4592-826f-3c85fc8fb6a7-test',
  PROD: '091bf775-529d-4036-a445-3a322675c69c',
};

/**
 * Retrieve the OneTrust domain script ID for the given environment. Not a secret -- it's embedded
 * directly in OneTrust's own script URL and `data-domain-script` attribute, so it's always visible
 * to anyone loading the page.
 * @description
 * By default it returns the non-production (test) domain script ID.
 */
export function getOneTrustDomainScriptId(env: Env) {
  return env === 'prod' ? oneTrustDomainScriptIds.PROD : oneTrustDomainScriptIds.TEST;
}

/**
 * Only the domain-scoped stub syncs authenticated consent, and it must also run for guests: it tags
 * their consent so OneTrust can carry it over (or clear it) when they log in or out. It exists only
 * once the domain script has been published with the consent group, so loaders fall back to
 * {@link ONETRUST_GENERIC_SDK_STUB_SRC} when it fails to load.
 */
export function getOneTrustDomainSdkStubSrc(domainScriptId: string) {
  return `https://cdn.cookielaw.org/consent/${domainScriptId}/otSDKStub.js`;
}

export const ONETRUST_GENERIC_SDK_STUB_SRC =
  'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
